import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Spinner, Tab, Table, Tabs } from 'react-bootstrap';
import { Box, Button as MuiButton, MenuItem, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import dateFormat from 'dateformat';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faFilePdf, faTrash } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { DatePickerWrapper } from '../../components/DatePickerWrapper';
import { Header } from '../../components/Header';
import { Navigation } from '../../components/Navigation';
import { SideBar } from '../../components/SideBar';
import { SideBarContext } from '../../context/SideBarContext';
import { CustomToastContent } from '../../components/CustomToastContent';
import { InfoModal } from '../../components/InfoModal';
import { toastOptionsTopRight } from '../../utils/Enums/ToastOptions';
import {
    FleetDriver,
    FleetKmReport,
    FleetStatus,
    FleetWaybill,
    FleetWaybillMonth,
    FleetWaybillStatus,
} from '../../models/fleet-models';
import {
    deleteFleetWaybill,
    fetchFleetDrivers,
    fetchFleetKmReport,
    fetchFleetStatus,
    fetchFleetWaybillMonthPdf,
    fetchFleetWaybillMonths,
    fetchFleetWaybillPdf,
    showFleetError,
    fetchFleetWaybills,
} from '../../utils/fleet-hooks';

const toDateValue = (date: Date) => dateFormat(date, 'yyyy-mm-dd');
const firstDayOfMonth = () => {
    const now = new Date();
    return toDateValue(new Date(now.getFullYear(), now.getMonth(), 1));
};
const formatDay = (day?: string) => (day ? dateFormat(new Date(`${day}T00:00:00`), 'dd/mm/yyyy') : '-');
const formatDateTime = (value?: string) => (value ? dateFormat(new Date(value), 'dd/mm/yyyy HH:MM') : '');
const formatKm = (value?: number) => (value === undefined || value === null ? '-' : Number(value).toFixed(1));
const formatDuration = (seconds?: number) => {
    if (!seconds) return '-';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.round((seconds % 3600) / 60);
    return `${hours} ч ${minutes.toString().padStart(2, '0')} мин`;
};

const STATUS_VARIANT: Record<FleetWaybillStatus, string> = {
    draft: 'secondary',
    signed: 'warning',
    approved: 'success',
};

/** Пътни листи и справка за километри от GPS Areon - за администратора. Мениджърите одобряват в приложението. */
export const FleetPage = () => {
    const { t } = useTranslation('translation', { keyPrefix: 'fleet' });
    const { sideBarVissible } = useContext(SideBarContext);
    const [status, setStatus] = useState<FleetStatus>();
    const [drivers, setDrivers] = useState<FleetDriver[]>([]);

    useEffect(() => {
        fetchFleetStatus().then(setStatus).catch(() => undefined);
        fetchFleetDrivers()
            .then((list) => setDrivers(Array.isArray(list) ? list.filter((driver) => driver.userId) : []))
            .catch(() => setDrivers([]));
    }, []);

    return (
        <div className={`d-flex wrapper ${sideBarVissible && 'sidebar-active'} w-100`}>
            <SideBar>
                <Navigation />
            </SideBar>
            <div className='d-flex content-wrapper flex-column justify-content-start align-items-center w-100'>
                <Header title={t('title')} />
                <div className='p-4 w-100'>
                    {status && !status.reachable && (
                        <Alert variant='danger' className='py-2'>
                            <FontAwesomeIcon icon={faExclamationTriangle} /> {t('gpsUnavailable')} {status.message}
                        </Alert>
                    )}
                    <Tabs defaultActiveKey='waybills' className='mb-3' mountOnEnter>
                        <Tab eventKey='waybills' title={t('waybills')}>
                            <WaybillsTab drivers={drivers} />
                        </Tab>
                        <Tab eventKey='months' title={t('monthlyWaybills')}>
                            <MonthlyWaybillsTab />
                        </Tab>
                        <Tab eventKey='km' title={t('kmReport')}>
                            <KmReportTab drivers={drivers} />
                        </Tab>
                    </Tabs>
                </div>
            </div>
        </div>
    );
};

// Филтрите са MUI полета като в „Начални салда“. Датите са през DatePickerWrapper (както в
// DepositTurnoverReport), за да следват езика на уеба, а не на браузъра: bg - дд/мм/гггг, en - мм/дд/гггг.
const dateMaskFor = (language?: string) => (language?.startsWith('en') ? 'MM/dd/yyyy' : 'dd/MM/yyyy');

const Filters = ({
    from, to, setFrom, setTo, drivers, driverUserId, setDriverUserId, onSearch, isLoading,
}: {
    from: string; to: string; setFrom: (value: string) => void; setTo: (value: string) => void;
    drivers: FleetDriver[]; driverUserId?: number; setDriverUserId: (value?: number) => void;
    onSearch: () => void; isLoading: boolean;
}) => {
    const { t, i18n } = useTranslation('translation', { keyPrefix: 'fleet' });
    const dateMask = dateMaskFor(i18n.language);
    const sortedDrivers = useMemo(
        () => [...drivers].sort((a, b) => (a.userName ?? a.name ?? '').localeCompare(b.userName ?? b.name ?? '', 'bg')),
        [drivers],
    );
    return (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', pt: 1, mb: 2 }}>
            <Box sx={{ width: 150 }}>
                <DatePickerWrapper
                    portalId='fleet-date-picker-portal'
                    selected={from}
                    maxDate={to}
                    dateMask={dateMask}
                    onChange={setFrom}
                    customInput={<TextField fullWidth size='small' label={t('from')} InputLabelProps={{ shrink: true }} />}
                />
            </Box>
            <Box sx={{ width: 150 }}>
                <DatePickerWrapper
                    portalId='fleet-date-picker-portal'
                    selected={to}
                    minDate={from}
                    dateMask={dateMask}
                    onChange={setTo}
                    customInput={<TextField fullWidth size='small' label={t('to')} InputLabelProps={{ shrink: true }} />}
                />
            </Box>
            <TextField
                select
                size='small'
                label={t('driver')}
                value={driverUserId ?? ''}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
                onChange={(event) => setDriverUserId(event.target.value === '' ? undefined : Number(event.target.value))}
                sx={{ minWidth: 240 }}
            >
                <MenuItem value=''>{t('allDrivers')}</MenuItem>
                {sortedDrivers.map((driver) => (
                    <MenuItem key={driver.userId} value={driver.userId}>{driver.userName ?? driver.name}</MenuItem>
                ))}
            </TextField>
            <MuiButton variant='contained' disabled={isLoading} onClick={onSearch} sx={{ height: 40, minWidth: 85 }}>
                {isLoading ? <Spinner animation='border' size='sm' /> : t('search')}
            </MuiButton>
        </Box>
    );
};

const WaybillsTab = ({ drivers }: { drivers: FleetDriver[] }) => {
    const { t } = useTranslation('translation', { keyPrefix: 'fleet' });
    const [from, setFrom] = useState(firstDayOfMonth());
    const [to, setTo] = useState(toDateValue(new Date()));
    const [driverUserId, setDriverUserId] = useState<number>();
    const [waybills, setWaybills] = useState<FleetWaybill[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [busyId, setBusyId] = useState<string>();
    const [waybillToDelete, setWaybillToDelete] = useState<FleetWaybill>();

    const load = useCallback(() => {
        setIsLoading(true);
        fetchFleetWaybills(from, to, driverUserId)
            .then((list) => setWaybills(Array.isArray(list) ? list : []))
            .catch(() => setWaybills([]))
            .finally(() => setIsLoading(false));
    }, [from, to, driverUserId]);

    useEffect(() => {
        load();
        // Само при отваряне; после - с бутона „Търси“.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openPdf = async (waybill: FleetWaybill) => {
        setBusyId(waybill.id);
        try {
            const pdf = await fetchFleetWaybillPdf(waybill.id);
            window.open(URL.createObjectURL(new Blob([pdf], { type: 'application/pdf' })), '_blank');
        } catch (error) {
            await showFleetError(error);
        } finally {
            setBusyId(undefined);
        }
    };

    const remove = async (waybill: FleetWaybill) => {
        setBusyId(waybill.id);
        try {
            await deleteFleetWaybill(waybill.id);
            toast.success(<CustomToastContent content={[t('waybillDeleted')]} />, toastOptionsTopRight);
            load();
        } catch (error) {
            await showFleetError(error);
        } finally {
            setBusyId(undefined);
        }
    };

    return (
        <>
            <Filters {...{ from, to, setFrom, setTo, drivers, driverUserId, setDriverUserId, isLoading }} onSearch={load} />
            {waybills.length === 0 ? (
                <div className='text-secondary'>{isLoading ? '' : t('noWaybills')}</div>
            ) : (
                <Table striped bordered hover size='sm' responsive>
                    <thead>
                        <tr>
                            <th>{t('day')}</th>
                            <th>{t('driver')}</th>
                            <th>{t('vehicle')}</th>
                            <th>{t('hours')}</th>
                            <th className='text-end'>{t('odometer')}</th>
                            <th className='text-end'>{t('odometerKm')}</th>
                            <th className='text-end'>{t('gpsKm')}</th>
                            <th>{t('status')}</th>
                            <th>{t('signatures')}</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {waybills.map((waybill) => (
                            <tr key={waybill.id}>
                                <td className='text-nowrap'>{formatDay(waybill.day)}</td>
                                <td>{waybill.userName ?? waybill.gpsDriverName}</td>
                                <td className='text-nowrap'>
                                    {waybill.plateNumber ?? waybill.deviceIdentifier}
                                    <VehicleModel make={waybill.make} model={waybill.model} />
                                </td>
                                <td className='text-nowrap'>{waybill.start} – {waybill.end}</td>
                                <td className='text-end text-nowrap'>{waybill.odometerStart} – {waybill.odometerEnd}</td>
                                <td className='text-end'>{waybill.odometerKm}</td>
                                <td className={`text-end text-nowrap ${waybill.mismatch ? 'text-danger fw-bold' : ''}`}
                                    title={waybill.mismatch ? t('mismatchHint', { difference: formatKm(waybill.differenceKm) }) : undefined}>
                                    {formatKm(waybill.gpsKm)}
                                    {waybill.mismatch && <> <FontAwesomeIcon icon={faExclamationTriangle} /></>}
                                </td>
                                <td><Badge bg={STATUS_VARIANT[waybill.status] ?? 'secondary'}>{t(`status_${waybill.status}`)}</Badge></td>
                                <td className='small'>
                                    {waybill.driverSignedBy && <div>{t('driverSigned')}: {waybill.driverSignedBy} {formatDateTime(waybill.driverSignedAt)}</div>}
                                    {waybill.approvedBy && <div>{t('approved')}: {waybill.approvedBy} {formatDateTime(waybill.approvedAt)}</div>}
                                </td>
                                <td className='text-nowrap'>
                                    <Button size='sm' variant='outline-primary' className='me-1' title={t('print')}
                                            disabled={busyId === waybill.id} onClick={() => openPdf(waybill)}>
                                        <FontAwesomeIcon icon={faFilePdf} />
                                    </Button>
                                    {waybill.canDelete && (
                                        <Button size='sm' variant='outline-danger' title={t('delete')}
                                                disabled={busyId === waybill.id} onClick={() => setWaybillToDelete(waybill)}>
                                            <FontAwesomeIcon icon={faTrash} />
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
            {/* Същият модал като при изтриване в таблиците на уеба (InfoModal), не window.confirm. */}
            <InfoModal
                show={!!waybillToDelete}
                translatedText={waybillToDelete && t('confirmDeleteWaybill', {
                    day: formatDay(waybillToDelete.day),
                    name: waybillToDelete.userName ?? waybillToDelete.gpsDriverName,
                })}
                onHide={(confirmed) => {
                    const waybill = waybillToDelete;
                    setWaybillToDelete(undefined);
                    if (confirmed && waybill) remove(waybill).then();
                }}
            />
        </>
    );
};

const VehicleModel = ({ make, model }: { make?: string; model?: string }) => {
    const text = [make, model].filter(Boolean).join(' ');
    return text ? <div className='small text-secondary'>{text}</div> : null;
};

/** Последните 12 месеца като гггг-мм, най-новият пръв. */
const lastMonths = () => {
    const now = new Date();
    return Array.from({ length: 12 }, (_, index) => {
        const date = new Date(now.getFullYear(), now.getMonth() - index, 1);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    });
};

/**
 * Месечните пътни листи: по един ред на кола за месеца, с PDF за печат (A4 легнал, с фирмата, ред за
 * всеки ден и сбор). Само за четене - дневните листи се попълват и подписват в приложението.
 */
const MonthlyWaybillsTab = () => {
    const { t, i18n } = useTranslation('translation', { keyPrefix: 'fleet' });
    const months = useMemo(lastMonths, []);
    const [month, setMonth] = useState(months[0]);
    const [rows, setRows] = useState<FleetWaybillMonth[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [busyKey, setBusyKey] = useState<string>();
    const showApprover = rows.some((row) => row.monthlyApproval);
    // Текущият месец още не е свършил - това е сведение, не пречка: сиво, не червено.
    const inProgress = month === months[0];

    const monthLabel = (value: string) => {
        const [year, monthNumber] = value.split('-').map(Number);
        const text = new Date(year, monthNumber - 1, 1).toLocaleDateString(i18n.language?.startsWith('en') ? 'en-GB' : 'bg-BG', {
            month: 'long',
            year: 'numeric',
        });
        return text.charAt(0).toUpperCase() + text.slice(1);
    };

    useEffect(() => {
        setIsLoading(true);
        fetchFleetWaybillMonths(month)
            .then((list) => setRows(Array.isArray(list) ? list : []))
            .catch(() => setRows([]))
            .finally(() => setIsLoading(false));
    }, [month]);

    const openPdf = async (row: FleetWaybillMonth) => {
        setBusyKey(row.deviceIdentifier);
        try {
            const pdf = await fetchFleetWaybillMonthPdf(row.deviceIdentifier, month);
            window.open(URL.createObjectURL(new Blob([pdf], { type: 'application/pdf' })), '_blank');
        } catch (error) {
            await showFleetError(error);
        } finally {
            setBusyKey(undefined);
        }
    };

    return (
        <>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', pt: 1, mb: 2 }}>
                <TextField
                    select
                    size='small'
                    label={t('month')}
                    value={month}
                    onChange={(event) => setMonth(event.target.value)}
                    sx={{ minWidth: 200 }}
                >
                    {months.map((value) => (
                        <MenuItem key={value} value={value}>{monthLabel(value)}</MenuItem>
                    ))}
                </TextField>
                {isLoading && <Spinner animation='border' size='sm' />}
            </Box>
            {showApprover && (
                <Alert variant='info' className='py-2'>{t('monthlyApprovalMode')}</Alert>
            )}
            {rows.length === 0 ? (
                <div className='text-secondary'>{isLoading ? '' : t('noMonthlyWaybills')}</div>
            ) : (
                <Table striped bordered hover size='sm' responsive>
                    <thead>
                        <tr>
                            <th>{t('vehicle')}</th>
                            <th>{t('drivers')}</th>
                            <th className='text-end'>{t('days')}</th>
                            <th className='text-end'>{t('waybillCount')}</th>
                            <th className='text-end'>{t('driverSignedCount')}</th>
                            <th className='text-end'>{t('approvedCount')}</th>
                            <th className='text-end'>{t('odometerKm')}</th>
                            <th>{t('status')}</th>
                            {showApprover && <th>{t('monthApprover')}</th>}
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.deviceIdentifier}>
                                <td className='text-nowrap'>
                                    {row.plateNumber ?? row.deviceIdentifier}
                                    <VehicleModel make={row.make} model={row.model} />
                                </td>
                                <td>{row.drivers.join(', ')}</td>
                                <td className='text-end'>{row.days}</td>
                                <td className='text-end'>{row.waybills}</td>
                                <td className={`text-end ${row.driverSigned < row.waybills ? 'text-danger fw-bold' : ''}`}>
                                    {row.driverSigned}
                                </td>
                                <td className='text-end'>{row.approved}</td>
                                <td className='text-end'>{row.odometerKm}</td>
                                <td><Badge bg={STATUS_VARIANT[row.status] ?? 'secondary'}>{t(`monthStatus_${row.status}`)}</Badge></td>
                                {showApprover && (
                                    <td>
                                        {row.status !== 'approved' && row.lastDriverName && (
                                            <div>{t('monthApproverOf', { name: row.lastDriverName })}</div>
                                        )}
                                        {row.blockedReason && (
                                            <div className={`small ${inProgress ? 'text-secondary' : 'text-danger'}`}>
                                                {row.blockedReason}
                                            </div>
                                        )}
                                    </td>
                                )}
                                <td className='text-nowrap'>
                                    <Button size='sm' variant='outline-primary' title={t('print')}
                                            disabled={busyKey === row.deviceIdentifier} onClick={() => openPdf(row)}>
                                        <FontAwesomeIcon icon={faFilePdf} />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
        </>
    );
};

const KmReportTab = ({ drivers }: { drivers: FleetDriver[] }) => {
    const { t } = useTranslation('translation', { keyPrefix: 'fleet' });
    const [from, setFrom] = useState(firstDayOfMonth());
    const [to, setTo] = useState(toDateValue(new Date()));
    const [driverUserId, setDriverUserId] = useState<number>();
    const [report, setReport] = useState<FleetKmReport>();
    const [isLoading, setIsLoading] = useState(false);
    const [showDays, setShowDays] = useState(false);

    const load = useCallback(() => {
        setIsLoading(true);
        fetchFleetKmReport(from, to, driverUserId)
            .then(setReport)
            .catch(() => setReport(undefined))
            .finally(() => setIsLoading(false));
    }, [from, to, driverUserId]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const totalKm = useMemo(() => (report?.totals ?? []).reduce((sum, total) => sum + Number(total.distanceKm ?? 0), 0), [report]);

    return (
        <>
            <Filters {...{ from, to, setFrom, setTo, drivers, driverUserId, setDriverUserId, isLoading }} onSearch={load} />
            {report?.completeFrom && (
                <Alert variant='warning' className='py-2'>{t('incompleteBefore', { day: formatDay(report.completeFrom) })}</Alert>
            )}
            {!report || report.totals.length === 0 ? (
                <div className='text-secondary'>{isLoading ? '' : t('noKm')}</div>
            ) : (
                <>
                    <Table striped bordered hover size='sm' responsive>
                        <thead>
                            <tr>
                                <th>{t('driver')}</th>
                                <th className='text-end'>{t('days')}</th>
                                <th className='text-end'>{t('distanceKm')}</th>
                                <th className='text-end'>{t('movingTime')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {report.totals.map((total) => (
                                <tr key={`${total.userId ?? total.gpsDriverName}`}>
                                    <td>{total.userName ?? `${total.gpsDriverName ?? t('noDriver')} (${t('notLinked')})`}</td>
                                    <td className='text-end'>{total.days}</td>
                                    <td className='text-end'>{formatKm(total.distanceKm)}</td>
                                    <td className='text-end'>{formatDuration(total.movingSeconds)}</td>
                                </tr>
                            ))}
                            <tr className='fw-bold'>
                                <td>{t('total')}</td>
                                <td />
                                <td className='text-end'>{formatKm(totalKm)}</td>
                                <td />
                            </tr>
                        </tbody>
                    </Table>
                    <Button variant='link' className='px-0' onClick={() => setShowDays(!showDays)}>
                        {showDays ? t('hideDays') : t('showDays')}
                    </Button>
                    {showDays && (
                        <Table striped bordered hover size='sm' responsive>
                            <thead>
                                <tr>
                                    <th>{t('day')}</th>
                                    <th>{t('driver')}</th>
                                    <th>{t('vehicle')}</th>
                                    <th className='text-end'>{t('distanceKm')}</th>
                                    <th className='text-end'>{t('urbanKm')}</th>
                                    <th className='text-end'>{t('ruralKm')}</th>
                                    <th className='text-end'>{t('movingTime')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {report.rows.map((row, index) => (
                                    <tr key={`${row.day}-${row.userId ?? row.gpsDriverName}-${row.deviceIdentifier}-${index}`}>
                                        <td className='text-nowrap'>{formatDay(row.day)}</td>
                                        <td>{row.userName ?? row.gpsDriverName ?? t('noDriver')}</td>
                                        <td className='text-nowrap'>{row.plateNumber ?? row.deviceIdentifier}</td>
                                        <td className='text-end'>{formatKm(row.distanceKm)}</td>
                                        <td className='text-end'>{formatKm(row.urbanKm)}</td>
                                        <td className='text-end'>{formatKm(row.ruralKm)}</td>
                                        <td className='text-end'>{formatDuration(row.movingSeconds)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </>
            )}
        </>
    );
};
